/**
 * `VisitMark` — a visit's calendar card at 28, 44 or 56 (`docs/visit-page.md`
 * §7.2, §7.3, §18; Slice 1's shared helper, lead round 3).
 *
 * Draws a `VisitMarkData`: the band carries the month of the first day (*OCT*)
 * and the number under it (*21*), or *TBD* and a dash while unscheduled; the
 * colour is the visit's status (§17.1: in progress teal, scheduled amber,
 * completed and closed green, the rest grey); a skipped visit is struck; a
 * visit of more than one day has a **second card** behind it; every
 * occurrence of a series wears the **loop badge**. Built on `landing-marks.tsx`
 * (`CalendarCard`), whose `visitMark` — the Visits list's — is unchanged.
 */
import type { VisitMarkData, VisitPageStatus } from '@rowted/shared'
import { CalendarCard, type MarkSize } from '../components/landing-marks.js'

/** The loop badge's ink by status, as the boards draw it (Boards 13 B, 14). */
const BADGE: Record<VisitPageStatus, string> = {
  unscheduled: 'rgb(var(--color-text-muted))',
  scheduled: 'rgb(var(--color-warn))',
  in_progress: 'rgb(var(--status-visitMark-in_progress-fg))',
  completed: 'rgb(var(--color-accent))',
  closed: 'rgb(var(--color-accent))',
  on_hold: 'rgb(var(--color-text-muted))',
  cancelled: 'rgb(var(--color-text-muted))',
  skipped: 'rgb(var(--color-text-muted))',
}

export function VisitMark({
  mark, size, multiDay = true, series = true, title,
}: {
  mark: VisitMarkData
  size: MarkSize
  /**
   * Draw the second card when `mark.multiDay` — on by default; a caller that
   * draws a day (not the whole visit) passes `false`.
   */
  multiDay?: boolean
  /** Draw the loop badge when `mark.series` — on by default. */
  series?: boolean
  title?: string
}) {
  const key = `--status-visitMark-${mark.status}`
  return (
    <CalendarCard
      date={mark.date}
      fg={`rgb(var(${key}-fg))`}
      bg={`rgb(var(${key}-bg))`}
      size={size}
      multiDay={multiDay && mark.multiDay}
      series={series && mark.series ? BADGE[mark.status] : null}
      skipped={mark.status === 'skipped'}
      {...(title ? { title } : {})}
    />
  )
}
